import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import styled, { type DefaultTheme } from "styled-components";

import { controlFocusRing, controlTransition } from "./controlStyles";
import { interactiveSurfaceCss, type InteractiveSurfaceSpec } from "./interactiveSurface";

export type CompositeListSize = "compact" | "regular";

const ROW_HEIGHT: Record<CompositeListSize, string> = {
  compact: "2rem",
  regular: "45px",
};

const PRIMARY_MIN_HEIGHT: Record<CompositeListSize, string> = {
  compact: "calc(2rem - 1px)",
  regular: "44px",
};

function transparentPrimarySurface(theme: DefaultTheme): InteractiveSurfaceSpec {
  return {
    rest: {
      background: "transparent",
      border: "transparent",
      foreground: theme.color.text.default,
    },
    hover: {
      background: "transparent",
      border: "transparent",
      foreground: theme.color.text.default,
    },
    active: {
      background: "transparent",
      border: "transparent",
      foreground: theme.color.text.default,
    },
    disabled: {
      background: "transparent",
      border: "transparent",
      foreground: theme.color.text.disabled,
    },
  };
}

/**
 * MediaAnnotationStudio의 네 composite list에서 사용 증거를 모으는 비공개 foundation.
 * 공개 ListView의 단일 listbox 계약을 넓히지 않고 semantic ul/li + sibling action cell을 조합한다.
 */
export const CompositeListSurface = styled.ul`
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0;
  /* 첫·마지막 행의 full-bleed 상태 면은 목록 surface의 단일 외곽 곡선을 따른다. */
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surfaceRaised};
  list-style: none;
`;

export const CompositeListRow = styled.li<{
  $size: CompositeListSize;
  $selected: boolean;
  $current?: boolean;
  $contextual?: boolean;
  $disabled?: boolean;
}>`
  --c6-composite-list-row-background: ${({ $selected, $contextual = false, theme }) =>
    $selected
      ? theme.color.bg.selected
      : $contextual
        ? theme.color.bg.surfaceRaisedHigh
        : theme.color.bg.surfaceRaised};

  position: relative;
  box-sizing: border-box;
  display: grid;
  width: 100%;
  min-width: 0;
  min-height: ${({ $size }) => ROW_HEIGHT[$size]};
  align-items: stretch;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.divider};
  background: var(--c6-composite-list-row-background);
  ${controlTransition("background-color")}

  &:hover {
    --c6-composite-list-row-background: ${({
      $disabled = false,
      $selected,
      $contextual = false,
      theme,
    }) =>
      $disabled
        ? $selected
          ? theme.color.bg.selected
          : $contextual
            ? theme.color.bg.surfaceRaisedHigh
            : theme.color.bg.surfaceRaised
        : $selected
          ? theme.color.bg.selectedHover
          : $contextual
            ? theme.color.bg.surfaceOverlay
            : theme.color.bg.surfaceRaisedHigh};
  }

  &:has([data-studio-list-primary]:active) {
    --c6-composite-list-row-background: ${({ $selected, theme }) =>
      $selected ? theme.color.bg.selectedActive : theme.color.bg.surfaceOverlay};
  }

  &::before {
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
    pointer-events: none;
    ${controlTransition("opacity")}
  }

  &::after {
    position: absolute;
    z-index: 1;
    inset: 0;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: ${({ $current = false, $selected }) => ($current && !$selected ? 1 : 0)};
    pointer-events: none;
    ${controlTransition("opacity")}
  }

  &:last-child {
    border-bottom-color: transparent;
  }

  @media (forced-colors: active) {
    &::before {
      background: Highlight;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::before,
    &::after {
      transition: none;
    }
  }
`;

export const CompositeListPrimaryButton = styled.button<{ $size: CompositeListSize }>`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: ${({ $size }) => PRIMARY_MIN_HEIGHT[$size]};
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: 0;
  font: inherit;
  text-align: left;
  ${({ theme }) => interactiveSurfaceCss(transparentPrimarySurface(theme))}

  &:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

export const CompositeListActionCell = styled.div<{ $size: CompositeListSize }>`
  z-index: 2;
  display: flex;
  width: 2rem;
  min-width: 2rem;
  height: 100%;
  align-items: stretch;

  & > span {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: stretch;
  }

  & button {
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: ${({ $size }) => PRIMARY_MIN_HEIGHT[$size]};
    border-radius: 0;
  }

  & button:focus-visible {
    ${controlFocusRing("-2px")}
  }

  & button:hover:enabled {
    /* Color-only list feedback must not clear a composed control's semantic background image
       (for example TrackingPlayButton's product-neutral tracking identifier). */
    background-color: ${({ theme }) => theme.color.bg.muted};
  }

  & button:not([data-studio-list-danger]):active:enabled {
    background-color: ${({ theme }) => theme.color.bg.selectedActive};
    transition-duration: 0ms;
  }

  /* Semantic warning actions opt back into their IconButton tone axis after the list-level
     neutral interaction layer above. */
  & button[data-studio-list-warning]:hover:enabled {
    background-color: ${({ theme }) => theme.color.bg.control.warningHover};
    color: ${({ theme }) => theme.color.text.onControl};
  }

  & button[data-studio-list-warning]:active:enabled {
    background-color: ${({ theme }) => theme.color.bg.control.warningActive};
    color: ${({ theme }) => theme.color.text.onControl};
    transition-duration: 0ms;
  }

  & button[aria-pressed]:hover:enabled,
  & button[aria-pressed]:active:enabled {
    color: ${({ theme }) => theme.color.text.default};
  }

  & button[data-studio-list-danger]:active:enabled {
    background-color: ${({ theme }) => theme.color.bg.control.dangerActive};
    color: ${({ theme }) => theme.color.text.onControl};
    transition-duration: 0ms;
  }
`;

export const CompositeListActionSlot = styled(CompositeListActionCell)<{
  $visible: boolean;
  $concealWhenHidden?: boolean;
}>`
  visibility: ${({ $visible, $concealWhenHidden = false }) =>
    !$visible && $concealWhenHidden ? "hidden" : "visible"};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  ${controlTransition("opacity")}

  ${CompositeListRow}:hover &,
  &:focus-within {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CompositeListActionGroup = styled.div`
  z-index: 2;
  display: flex;
  min-width: 0;
  align-items: stretch;
  gap: 0;
`;

export interface CompositeListPrimaryNavigationProps {
  readonly ref: (element: HTMLButtonElement | null) => void;
  readonly tabIndex: number;
  readonly "aria-pressed": boolean;
  readonly onClick: () => void;
  readonly onKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
}

export function useCompositeListNavigation<T>({
  items,
  getKey,
  selectedKey,
  onSelect,
  scrollContainerRef,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  selectedKey: string | null;
  onSelect: (item: T, index: number) => void;
  scrollContainerRef: { readonly current: HTMLElement | null };
}) {
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const navigationState = useRef({ items, getKey, onSelect });
  const handlerCache = useRef(
    new Map<string, Pick<CompositeListPrimaryNavigationProps, "ref" | "onClick" | "onKeyDown">>(),
  );
  navigationState.current = { items, getKey, onSelect };
  const selectedExists = items.some((item) => getKey(item) === selectedKey);
  const tabStopKey = selectedExists ? selectedKey : items.length > 0 ? getKey(items[0] as T) : null;

  useEffect(() => {
    const selected = selectedKey ? itemRefs.current.get(selectedKey) : null;
    if (selected) scrollCompositeListItemIntoView(selected, scrollContainerRef.current);
  }, [scrollContainerRef, selectedKey]);

  useEffect(() => {
    const liveKeys = new Set(items.map(getKey));
    for (const key of handlerCache.current.keys()) {
      if (!liveKeys.has(key)) handlerCache.current.delete(key);
    }
  }, [getKey, items]);

  const selectAt = (index: number) => {
    const current = navigationState.current;
    const item = current.items[index];
    if (!item) return;
    current.onSelect(item, index);
    itemRefs.current.get(current.getKey(item))?.focus({ preventScroll: true });
  };

  const handlersFor = (key: string) => {
    const cached = handlerCache.current.get(key);
    if (cached) return cached;
    const handlers: Pick<CompositeListPrimaryNavigationProps, "ref" | "onClick" | "onKeyDown"> = {
      ref: (element) => {
        if (element) itemRefs.current.set(key, element);
        else itemRefs.current.delete(key);
      },
      onClick: () => {
        const current = navigationState.current;
        const index = current.items.findIndex((item) => current.getKey(item) === key);
        const item = current.items[index];
        if (item) current.onSelect(item, index);
      },
      onKeyDown: (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const current = navigationState.current;
        const index = current.items.findIndex((item) => current.getKey(item) === key);
        if (index < 0) return;
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            selectAt(Math.min(current.items.length - 1, index + 1));
            break;
          case "ArrowUp":
            event.preventDefault();
            selectAt(Math.max(0, index - 1));
            break;
          case "Home":
            event.preventDefault();
            selectAt(0);
            break;
          case "End":
            event.preventDefault();
            selectAt(current.items.length - 1);
            break;
          default:
            break;
        }
      },
    };
    handlerCache.current.set(key, handlers);
    return handlers;
  };

  return {
    getPrimaryNavigationProps(item: T, index: number): CompositeListPrimaryNavigationProps {
      void index;
      const key = getKey(item);
      const selected = key === selectedKey;
      const handlers = handlersFor(key);

      return {
        ...handlers,
        tabIndex: key === tabStopKey ? 0 : -1,
        "aria-pressed": selected,
      };
    },
  };
}

function scrollCompositeListItemIntoView(item: HTMLElement, container: HTMLElement | null) {
  if (!container) return;
  const itemRect = item.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  if (itemRect.top < containerRect.top) {
    container.scrollTop -= containerRect.top - itemRect.top;
  } else if (itemRect.bottom > containerRect.bottom) {
    container.scrollTop += itemRect.bottom - containerRect.bottom;
  }
}
